import { useEffect, useRef, useState } from "react";

//!COMPONENTS
import ChatSpace from "./components/chat";
import Conversation from "./components/conversation";

//!REDUX
import { useDispatch, useSelector } from "react-redux";
import { setCurrentChat, toggleAppChat } from "store/AppChat/appChat";

//!ASSETS
import ArrowBack from "../../assets/images/app/arrow_back.svg";

//!STYLE
import { Drawer } from "antd";
import { UserRoles } from "common/enums";
import { useGetAllRoomsQuery } from "store/services/chat";
import s from "./assets/appChat.module.css";
import Message from "./components/message";

import { skipToken } from "@reduxjs/toolkit/query";

const AppChat = () => {
  //!HOOKS
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { openAppChat, currentChat, startChat } = useSelector((state) => state.appChat);
  const { socket, talkId, conversation: conversationStore } = useSelector((state) => state.appChat);
  const {
    data: conversations,
    isLoading,
    isError,
    refetch,
  } = useGetAllRoomsQuery(
    user?._id ?? skipToken
  );

  //!STATES
  const [conversation, setConversation] = useState<any[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);

  // Screen size states
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);
  const [isXsScreen, setIsXsScreen] = useState(window.innerWidth > 767 && window.innerWidth <= 1151);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth > 1151 && window.innerWidth <= 1321);
  const [isMediumScreen, setIsMediumScreen] = useState(window.innerWidth > 1321 && window.innerWidth <= 1606);
  const [isNormalScreen, setIsNormalScreen] = useState(window.innerWidth > 1606 && window.innerWidth <= 1799);

  const handleResize = () => {
    setIsMobile(window.innerWidth <= 767);
    setIsXsScreen(window.innerWidth > 767 && window.innerWidth <= 1151);
    setIsSmallScreen(window.innerWidth > 1151 && window.innerWidth <= 1321);
    setIsMediumScreen(window.innerWidth > 1321 && window.innerWidth <= 1606);
    setIsNormalScreen(window.innerWidth > 1606 && window.innerWidth <= 1799);
  };

  const [isAttachment, setIsAttachment] = useState<boolean>(false);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Improved message deduplication system
  const messageIdsRef = useRef<Set<string>>(new Set());
  const typingUsersRef = useRef(new Set());

  // Socket connection and cleanup
  useEffect(() => {
    // Reset state when component mounts or talkId changes
    if (talkId) {
      setConversation([]);
      messageIdsRef.current = new Set();
    }

    return () => {
      // Clear states on unmount
      setConversation([]);
      messageIdsRef.current = new Set();
      typingUsersRef.current = new Set();
    };
  }, [talkId]);

  // Socket message event listeners
  useEffect(() => {
    if (!socket) return;

    // Handle new messages
    const handleNewMessage = async (msgResponse: any) => {
      // Skip if not for current chat
      if (!talkId || !msgResponse?.data?.talkId || msgResponse?.data?.talkId !== talkId) {
        return;
      }

      console.log("msgResponse : ", msgResponse)

      // Create a reliable ID for deduplication
      const msg = msgResponse?.data;
      const messageId = msg?._id ||
        `${msg?.senderId?._id || msg?.sender}-${msg?.message}-${msg?.createdAt || Date.now()}`;

      // Skip duplicates
      if (messageIdsRef.current.has(messageId)) {
        return;
      }

      // Add to seen messages
      messageIdsRef.current.add(messageId);

      // Update conversation safely
      setConversation(prev => [...prev, msg]);

      refetch();

      // Reset typing state for the sender
      if (msg?.sender) {
        typingUsersRef.current.delete(msg.sender);
        updateTypingState();
      }
    };

    // Handle typing indicators
    const handleUserTyping = ({ isTyping, userId }: any) => {
      if (!userId || !talkId) return;

      if (isTyping) {
        typingUsersRef.current.add(userId);
      } else {
        typingUsersRef.current.delete(userId);
      }

      updateTypingState();
    };

    const updateTypingState = () => {
      // If any users are typing, set the state to true
      setIsTyping(typingUsersRef.current.size > 0);
    };

    // Handle previous messages loaded
    const handlePreviousMessages = (response: any) => {
      if (!response?.data || !Array.isArray(response.data)) {
        console.error("Invalid previous messages data:", response);
        return;
      }

      // Filter out duplicates and add to tracking
      const uniqueMessages = response.data.filter((msg: any) => {
        const id = msg?._id ||
          `${msg?.senderId?._id || msg?.sender}-${msg?.message}-${msg?.createdAt || Date.now()}`;

        if (messageIdsRef.current.has(id)) {
          return false;
        }

        messageIdsRef.current.add(id);
        return true;
      });

      // Replace the conversation with these messages
      setConversation(uniqueMessages);
    };

    // Subscribe to socket events
    socket.on("newMessage", handleNewMessage);
    socket.on("userTyping", handleUserTyping);
    socket.on("previousMessagesLoaded", handlePreviousMessages);

    // Cleanup function
    return () => {
      socket.off("newMessage", handleNewMessage);
      socket.off("userTyping", handleUserTyping);
      socket.off("previousMessagesLoaded", handlePreviousMessages);
    };
  }, [socket, talkId, refetch]);

  const isParent = user?.roles?.includes(UserRoles.PARENT);
  const isOrganism = user?.roles?.includes(UserRoles.ORGANISME);

  return (
    <Drawer
      className="drawer"
      onClose={() => { dispatch(toggleAppChat(false)); dispatch(setCurrentChat(null)); }}
      visible={openAppChat}
      width={isMobile ? "100%" : isXsScreen ? "100%" : isSmallScreen ? "100%" : isMediumScreen ? "100%" : isNormalScreen ? "80%" : "75%"}
      title={
        <div className={s.drawerTitle}>
          Messagerie
        </div>
      }
      headerStyle={{ border: "none", paddingTop: "50px", paddingLeft: isMobile ? "20px" : "40px", backgroundColor: "transparent" }}
      closeIcon={
        <img
          src={ArrowBack}
          height={isMobile ? 10 : 18}
          alt="Close"
        />
      }
      drawerStyle={{ backgroundColor: "#FFF", borderTopLeftRadius: isMobile ? "0px" : "30px", borderBottomLeftRadius: isMobile ? "0px" : "30px", boxShadow: "-3px 3px 34px #39393929" }}
    >
      <div className={s.app__container}>
        <div className={s.row}>
          <div className={s.conversation}>
            <Conversation
              setConversation={setConversation}
              conversations={conversations}
              isLoading={isLoading}
              isError={isError}
            />
          </div>

          <div className={`${s.chat} ${currentChat && s.active}`}>
            <div className={s.chat__area}>
              <ChatSpace
                conversation={conversation}
                isTyping={isTyping}
                isBlocked={isBlocked}
                setIsBlocked={setIsBlocked}
                isAttachment={isAttachment}
              />
            </div>
            {startChat && <div className={s.chat__area__footer}>
              <Message
                conversation={conversation}
                setConversation={setConversation}
                setIsTyping={setIsTyping}
                isBlocked={isBlocked}
                setIsAttachment={setIsAttachment}
              />
            </div>}
          </div>
        </div>
        <div className={s.conditionsBox}>
          En utilisant notre chat {isParent && isOrganism ? "vous acceptez" : "tu acceptes"} nos{" "}
          <a
            href="/assets/files/mentions-legales.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            mentions légales
          </a>{" "}
          et notre{" "}
          <a
            href="/assets/files/politique-confidentialite.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            politique de confidentialité
          </a>.
        </div>

      </div>
    </Drawer>
  );
};

export default AppChat;
